export const POPULATION_COLOR_PALETTES: Record<string, Record<number, string>> = {
  denim: {
    50: "#f2f7fc",
    100: "#e2ecf8",
    200: "#c7daf0",
    300: "#a0bfe4",
    400: "#6d9dd5",
    500: "#3673ba",
    600: "#2c5f9b",
    700: "#224e81",
    800: "#193d66",
    900: "#122e4e",
    950: "#0a1d33",
  },
  blue: {
    50: "#eff6ff",
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2563eb",
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#1e3a8a",
    950: "#172554",
  },
  teal: {
    50: "#f0fdfa",
    100: "#ccfbf1",
    200: "#99f6e4",
    300: "#5eead4",
    400: "#2dd4bf",
    500: "#14b8a6",
    600: "#0d9488",
    700: "#0f766e",
    800: "#115e59",
    900: "#134e4a",
    950: "#042f2e",
  },
  purple: {
    50: "#faf5ff",
    100: "#f3e8ff",
    200: "#e9d5ff",
    300: "#d8b4fe",
    400: "#c084fc",
    500: "#a855f7",
    600: "#9333ea",
    700: "#7e22ce",
    800: "#6b21a8",
    900: "#581c87",
    950: "#3b0764",
  },
  amber: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03",
  },
  rose: {
    50: "#fff1f2",
    100: "#ffe4e6",
    200: "#fecdd3",
    300: "#fda4af",
    400: "#fb7185",
    500: "#f43f5e",
    600: "#e11d48",
    700: "#be123c",
    800: "#9f1239",
    900: "#881337",
    950: "#4c0519",
  },
  slate: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
    950: "#020617",
  },
};

export const POPULATION_PALETTE_NAMES = Object.keys(POPULATION_COLOR_PALETTES);

export const POPULATION_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

export const POPULATION_AUTO_COLORS = [
  POPULATION_COLOR_PALETTES.denim[700],
  POPULATION_COLOR_PALETTES.teal[500],
  POPULATION_COLOR_PALETTES.amber[500],
  POPULATION_COLOR_PALETTES.rose[500],
  POPULATION_COLOR_PALETTES.purple[500],
  POPULATION_COLOR_PALETTES.denim[400],
  POPULATION_COLOR_PALETTES.teal[800],
  POPULATION_COLOR_PALETTES.slate[400],
];

interface PopulationLike {
  search_group?: { token?: string; color?: string | null } | null;
}

export function populationColor(token: string | null | undefined, populations: PopulationLike[] | null | undefined) {
  if (!token || token === "all") return POPULATION_AUTO_COLORS[0];
  const index = (populations || []).findIndex((p) => p.search_group?.token === token);
  if (index === -1) return POPULATION_AUTO_COLORS[0];
  const searchGroup = populations![index].search_group;
  return searchGroup?.color || POPULATION_AUTO_COLORS[(index + 1) % POPULATION_AUTO_COLORS.length];
}

export const SERIES_COLORS = ["#3673ba", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

export const SENTIMENT_COLORS = { positive: "#10b981", neutral: "#cbd5e1", negative: "#f43f5e" };

export const SENTIMENT_SEGMENTS = [
  { key: "negative", label: "Negative", color: SENTIMENT_COLORS.negative },
  { key: "neutral", label: "Neutral", color: SENTIMENT_COLORS.neutral },
  { key: "positive", label: "Positive", color: SENTIMENT_COLORS.positive },
] as const;

export const SENTIMENT_BY_CODE: Record<number, { label: string; color: string }> = {
  1: { label: "Positive", color: SENTIMENT_COLORS.positive },
  2: { label: "Neutral", color: SENTIMENT_COLORS.neutral },
  3: { label: "Negative", color: SENTIMENT_COLORS.negative },
};

export const NPS_COLORS = { detractors: "#ef4444", passives: "#fcd34d", promoters: "#10b981" };

export const SATISFACTION_STOPS = { low: "#dc2626", mid: "#eab308", high: "#16a34a" };

export const COMPLETION_COLORS = { ok: "#cfdaea", conditional: "#e9eef5", drop: "#0e3554", screenout: "#e8542f" };

export const MUTED_POINT_COLORS = { fill: "#d1d5db", stroke: "#9ca3af" };

export const GEO_MAP_FILLS = { empty: "#f1f5f9", lowBase: "#e2e8f0" };

export const OFFICE_PREVIEW_COLORS = ["#4472C4", "#ED7D31", "#A5A5A5", "#FFC000", "#5B9BD5", "#70AD47"];

export const REPORT_THEME_DEFAULT_COLORS = ["#05AF90", "#0069A2", "#D15B82", "#DB7C60", "#9E51BF", "#C35656"];
